import { useState, useEffect, useRef } from "react";
import { FaChevronUp, FaChevronDown, FaUserPlus } from "react-icons/fa";
import AdminService from "../services/adminService";
import LoadingForButton from "./LoadingForButton";
import { useDispatch, useSelector } from "react-redux";
import { refreshDataGuest } from "../store/guestSlice";
import { useTranslation } from "react-i18next";
import UpgradePrompt from "./UpgradePrompt";

const GUEST_FREE_LIMIT = 50;

const genderOptions = [
    { value: "male", labelKey: "male" },
    { value: "female", labelKey: "female" },
];

const AddGuest = ({ guests = [], tables, setActiveForm }) => {
    const { t } = useTranslation();
    const user = useSelector((state) => state.userStore.user);

    const [formData, setFormData] = useState({
        fullName: "",
        // gender: ""
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [invalidCredentials, setInvalidCredentials] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const genderRef = useRef(null);
    const dispatch = useDispatch();

    const isPremium = user?.subscription === "premium" && user?.subscriptionStatus === "active";
    const isAtLimit = !isPremium && guests.length >= GUEST_FREE_LIMIT;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (openDropdown === "gender" && genderRef.current && !genderRef.current.contains(e.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: false }));
        setInvalidCredentials("");
        setSuccessMessage("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isAtLimit) return;

        setLoading(true);
        setErrors({});

        const newErrors = {};
        const nameRegex = /^[a-zA-ZčćđšžČĆĐŠŽ\s]+$/;

        if (!formData.fullName.trim()) {
            newErrors.fullName = t("fullNameRequired");
        } else if (!nameRegex.test(formData.fullName.trim())) {
            newErrors.fullName = t("fullNameInvalid");
        } else if (formData.fullName.trim().length > 50) {
            newErrors.fullName = t("maxLengthError", { count: 50 });
        } else if (formData.fullName.trim().split(/\s+/).length < 2) {
            newErrors.fullName = t("fullNameTwoWords");
        }

        // if (!formData.gender) newErrors.gender = t("genderRequired");

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setLoading(false);
            return;
        }

        AdminService.addGuest(formData)
            .then(() => {
                setLoading(false);
                dispatch(refreshDataGuest());
                setFormData({ fullName: "" });
                setSuccessMessage(t("guestAddedSuccess"));
                setTimeout(() => setSuccessMessage(""), 3000);
            })
            .catch((err) => {
                if (err.response?.data?.message.includes("already exists")) {
                    setInvalidCredentials(t("guestExistsError"));
                }
                else if (err.response?.data?.message.includes("guest limit")) {
                    setInvalidCredentials(t("guestLimitReached"));
                }
                else {
                    setInvalidCredentials(t("genericError"));
                }
                setLoading(false);
            });
    };

    return (
        <div className="flex justify-center pt-20 pb-10 lg:ml-[300px] overflow-y-auto">
            <div className="relative p-4 rounded-lg w-full max-w-2xl mx-auto">
                <h2 className="text-2xl font-semibold text-center mb-5 flex items-center justify-center">
                    <FaUserPlus className="mr-2 text-gray-700" /> {t("addGuestTitle")}
                </h2>
                <p className="text-gray-600 mb-7">{t("addGuestSubtitle")}</p>

                {isAtLimit && (
                    <UpgradePrompt
                        reason="guest"
                        currentCount={guests.length}
                        limit={GUEST_FREE_LIMIT}
                        otherCount={tables.length}       // trebaš proslijediti tables prop
                        setActiveForm={() => setActiveForm("addTable")}
                    />
                )}

                <form onSubmit={handleSubmit} className={`flex flex-col space-y-7 mb-10 ${isAtLimit ? "opacity-50 pointer-events-none select-none" : ""}`}>
                    <fieldset disabled={isAtLimit} className="flex flex-col space-y-7">
                        <div className="flex flex-col">
                            <label className="font-semibold flex items-center mb-1">{t("fullName")} *</label>
                            <input
                                type="text"
                                name="fullName"
                                maxLength={50}
                                autoFocus={!isAtLimit}
                                value={formData.fullName}
                                onChange={handleChange}
                                className={`inputWedding !pl-4 ${errors.fullName ? "!border-red-500" : ""}`}
                            />
                            {errors.fullName && <p className="text-red-500 text-sm">{errors.fullName}</p>}
                        </div>

                        {invalidCredentials && (
                            <p className="bg-red-100 rounded-lg p-2 text-red-700 text-center my-4">
                                {invalidCredentials}
                            </p>
                        )}

                        {successMessage && (
                            <p className="bg-green-100 rounded-lg p-2 text-green-700 text-center my-4">
                                {successMessage}
                            </p>
                        )}

                        <button type="submit" disabled={loading || isAtLimit} className="submitButton w-full">
                            {loading ? <LoadingForButton /> : t("addGuestButton")}
                        </button>
                    </fieldset>
                </form>
            </div>
        </div>
    );
};

export default AddGuest;
